// ============================================================================
// stage1-header-seeds.ts
// ----------------------------------------------------------------------------
// What an email's From header may seed into the Stage 1 client fields, and who
// owns what it seeded.
//
// RFQ 903025 (2026-10-18): the request arrived through a delivery platform. The
// email's From was the platform's no-reply address, and the import copied its
// display name, address and domain into the client name, client email and client
// company. Three things made that stick:
//
//   1. a delivery or no-reply sender is TRANSPORT, never the client, and it was
//      treated as one;
//   2. the seeded values then counted as filled, so the RFQ's real client and
//      requester (read from the request itself, by the contact extraction) were
//      judged "already populated" and never written, and the identity extraction
//      was skipped altogether once an address and a quote reference were in;
//   3. nothing recorded that those values were machine seeds rather than the
//      operator's.
//
// Fixes, all at the producer:
//
//   - A transport sender seeds NOTHING (matched by what the address says it is, a
//     no-reply / mailer-daemon / notification mailbox, never by a customer's name).
//   - An ordinary sender's seed is recorded with the fingerprint of the value that
//     was written. While the field still holds EXACTLY that value, a verified
//     extraction may replace it. A fingerprint proves current equality and nothing
//     more: it cannot tell an untouched seed from one the operator retyped
//     identically, or edited and then put back, so those stay replaceable. Any
//     DIFFERENT value is protected, including one typed while the extraction runs,
//     because every automatic write re-reads the live form when it commits.
//   - A field with no seed record is never replaceable (legacy and unknown values are
//     left exactly as they are).
//
// Scope: this corrects the producer for imports made from now on. It rewrites nothing
// that is already stored, so a project (or a title) that followed a transport sender
// before this change still reads as it was saved. The only effect on such a project is
// that its transport address no longer drives the email-domain rule
// (buildClassificationVerifierInput).
// ============================================================================
import { fingerprintSourceContent } from '../utils/source-provenance';

type Form = Record<string, unknown>;

export const HEADER_SEED_FIELDS = ['clientName', 'clientEmail', 'clientCompany'] as const;
export type HeaderSeedField = typeof HEADER_SEED_FIELDS[number];

interface HeaderSeedReceipt {
    version: 1;
    /** The sender address the seeds came from (for the audit trail). */
    sender: string;
    /** Per field: the fingerprint of the exact value that was written. */
    fields: Partial<Record<HeaderSeedField, string>>;
}

const RECEIPT_KEY = 'stage1HeaderSeeds';

// A mailbox that exists to send, not to be answered: the address says so. The
// `+tag` of a plus-addressed mailbox (`no-reply+abc123@`, `bounces+9f2@`) is not part
// of what the mailbox IS, so it is dropped before matching.
const TRANSPORT_LOCAL_PART = new RegExp(
    '^(?:'
    + 'no[-_.]?reply|do[-_.]?not[-_.]?reply|do[-_.]?not[-_.]?respond|no[-_.]?response'
    + '|mailer[-_.]?daemon|postmaster|bounces?(?:[-_.=].*)?'
    + '|notifications?|notify|alerts?|automated?|auto[-_.]?(?:mailer|reply|notify)'
    + '|system|robot|daemon|mail[-_.]?delivery|delivery[-_.]?(?:status|service)'
    + ')$',
    'i',
);
const FREE_MAIL_DOMAIN = /^(gmail|yahoo|hotmail|outlook|live|icloud|aol|me)\./i;
const ADDRESS = '[^\\s@<>",;]+@[^\\s@<>",;]+';

function text(value: unknown): string { return typeof value === 'string' ? value.trim() : ''; }

/**
 * A From header read as what it is: either `Display Name <address>` (the address
 * is whatever sits inside the angle brackets, the name is everything before them)
 * or a BARE address with no name at all. Anything else is not an address and yields
 * null, so it can neither seed nor be mistaken for a transport sender.
 */
function parseFromHeader(emailFrom: unknown): { name: string; address: string } | null {
    const raw = text(emailFrom);
    if (!raw) return null;
    const angled = raw.match(new RegExp(`^(.*?)<\\s*(${ADDRESS})\\s*>$`));
    if (angled) {
        const name = (angled[1] || '').trim().replace(/^"(.*)"$/s, '$1').trim();
        return { name, address: (angled[2] || '').trim().toLowerCase() };
    }
    if (new RegExp(`^${ADDRESS}$`).test(raw)) return { name: '', address: raw.toLowerCase() };
    return null;
}

/** The address in a From header ("Name <a@b.c>", "a@b.c"), lower-cased, or ''. */
export function senderAddress(emailFrom: unknown): string {
    return parseFromHeader(emailFrom)?.address || '';
}

/** A delivery-service / no-reply sender. Decided by the address alone. */
export function isTransportSender(emailFrom: unknown): boolean {
    const address = senderAddress(emailFrom);
    if (!address) return false;
    const mailbox = (address.split('@')[0] || '').replace(/\+.*$/, '');
    return TRANSPORT_LOCAL_PART.test(mailbox);
}

/**
 * The values a From header may seed, or nothing for a transport sender. A display
 * name seeds the client name; the address seeds the client email; and the domain's
 * first label, unless it is a free-mail provider, is offered as a company hint. A
 * bare address (no display name) seeds only the address.
 */
export function computeHeaderSeeds(emailFrom: unknown): Partial<Record<HeaderSeedField, string>> {
    const parsed = parseFromHeader(emailFrom);
    if (!parsed || isTransportSender(emailFrom)) return {};
    const seeds: Partial<Record<HeaderSeedField, string>> = { clientEmail: parsed.address };
    if (parsed.name && !parsed.name.includes('@')) seeds.clientName = parsed.name;
    if (parsed.name) {
        const domain = parsed.address.split('@')[1] || '';
        if (domain && !FREE_MAIL_DOMAIN.test(domain)) {
            const hint = (domain.split('.')[0] || '').replace(/[-_]/g, ' ');
            if (hint) seeds.clientCompany = hint.charAt(0).toUpperCase() + hint.slice(1);
        }
    }
    return seeds;
}

function readReceipt(form: Form | null | undefined): HeaderSeedReceipt | null {
    const value = form?.[RECEIPT_KEY] as HeaderSeedReceipt | undefined;
    return value && value.version === 1 && value.fields && typeof value.fields === 'object' ? value : null;
}

/**
 * Write the seeds into `prev`, only into fields that are EMPTY in `prev` right now
 * (a functional-update caller sees the live form), and record each one's owner.
 * Returns `prev` itself when nothing was written.
 */
export function applyHeaderSeeds(
    prev: Form,
    seeds: Partial<Record<HeaderSeedField, string>>,
    sender: unknown,
): Form {
    const written: Partial<Record<HeaderSeedField, string>> = {};
    for (const field of HEADER_SEED_FIELDS) {
        const value = text(seeds[field]);
        if (value && !text(prev[field])) written[field] = value;
    }
    if (Object.keys(written).length === 0) return prev;
    const prior = readReceipt(prev);
    const fields = { ...(prior?.fields || {}) };
    for (const [field, value] of Object.entries(written)) fields[field as HeaderSeedField] = fingerprintSourceContent(value);
    return {
        ...prev,
        ...written,
        [RECEIPT_KEY]: { version: 1, sender: senderAddress(sender), fields } satisfies HeaderSeedReceipt,
    };
}

/** True while `field` still holds exactly the value the header seeded: nobody has touched it. */
export function isUnchangedHeaderSeed(form: Form | null | undefined, field: string): boolean {
    const receipt = readReceipt(form);
    const recorded = receipt?.fields?.[field as HeaderSeedField];
    const current = text(form?.[field]);
    return !!recorded && !!current && recorded === fingerprintSourceContent(current);
}

/**
 * The form as automatic extraction should see it: every UNCHANGED header seed is
 * treated as empty, everything else (operator edits, legacy values, values with no
 * seed record) is left exactly as it is.
 */
export function withoutUnchangedHeaderSeeds<T extends Form>(form: T): T {
    const receipt = readReceipt(form);
    if (!receipt) return form;
    let next: Form | null = null;
    for (const field of HEADER_SEED_FIELDS) {
        if (isUnchangedHeaderSeed(form, field)) {
            next = next || { ...form };
            next[field] = '';
        }
    }
    return (next || form) as T;
}
